/**
 * Procedural textures, ported verbatim from the concept renderer (concept L129–L219). Every texture is generated at
 * load from seeded canvas drawing, so the build ships no image files for them.
 */
import { CanvasTexture, RepeatWrapping, SRGBColorSpace } from 'three';
import { mulberry32 } from '../core/rng.ts';

const TAU = Math.PI * 2;

function canvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

function ctx(c: HTMLCanvasElement): CanvasRenderingContext2D {
  const g = c.getContext('2d');
  if (!g) throw new Error('2D canvas unavailable');
  return g;
}

/** Tileable water normal map from 14 random plane waves (concept L131). */
export function waterNormalTex(seed: number, size: number): CanvasTexture {
  const cv = canvas(size, size);
  const g = ctx(cv);
  const img = g.createImageData(size, size);
  const D = img.data;
  const r = mulberry32(seed);
  const waves: [number, number, number, number][] = [];
  const amp = 1.1 / 14;
  for (let k = 0; k < 14; k++) {
    const fx = Math.round((r() * 2 - 1) * 7);
    let fy = Math.round((r() * 2 - 1) * 7);
    if (!fx && !fy) fy = 1;
    waves.push([fx, fy, r() * TAU, amp / Math.sqrt(fx * fx + fy * fy)]);
  }
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++) {
      let dx = 0;
      let dy = 0;
      for (const wv of waves) {
        const ph = (TAU * (wv[0] * x + wv[1] * y)) / size + wv[2];
        const cs = Math.cos(ph) * wv[3] * TAU;
        dx += cs * wv[0];
        dy += cs * wv[1];
      }
      const nx = -dx * 0.35;
      const ny = -dy * 0.35;
      const nz = 1;
      const l = Math.sqrt(nx * nx + ny * ny + nz * nz);
      const i = (y * size + x) * 4;
      D[i] = ((nx / l) * 0.5 + 0.5) * 255;
      D[i + 1] = ((ny / l) * 0.5 + 0.5) * 255;
      D[i + 2] = ((nz / l) * 0.5 + 0.5) * 255;
      D[i + 3] = 255;
    }
  g.putImageData(img, 0, 0);
  const t = new CanvasTexture(cv);
  t.wrapS = t.wrapT = RepeatWrapping;
  return t;
}

/** Shoreline foam strip, tileable along u (concept L151). */
export function foamTex(): CanvasTexture {
  const cv = canvas(512, 64);
  const g = ctx(cv);
  const r = mulberry32(7);
  for (let i = 0; i < 650; i++) {
    const u = r() * 512;
    const v = Math.pow(r(), 2.4) * 64;
    const rad = 0.8 + r() * 3.8;
    const a = (0.18 + r() * 0.6) * (1 - v / 70);
    g.fillStyle = 'rgba(255,255,255,' + a.toFixed(3) + ')';
    for (let o = -512; o <= 512; o += 512) {
      g.beginPath();
      g.ellipse(u + o, v, rad * 2.4, rad, 0, 0, TAU);
      g.fill();
    }
  }
  const t = new CanvasTexture(cv);
  t.wrapS = RepeatWrapping;
  t.colorSpace = SRGBColorSpace;
  return t;
}

/** Terrain detail and bump texture (GDD §3.3; concept L160). */
export function groundTex(): CanvasTexture {
  const cv = canvas(256, 256);
  const g = ctx(cv);
  const r = mulberry32(11);
  g.fillStyle = 'rgb(222,222,222)';
  g.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 2600; i++) {
    const x = r() * 256;
    const y = r() * 256;
    const rad = 0.6 + r() * 3.2;
    const v = Math.floor(170 + r() * 85);
    g.fillStyle = 'rgba(' + v + ',' + v + ',' + v + ',' + (0.25 + r() * 0.5).toFixed(2) + ')';
    for (let ox = -256; ox <= 256; ox += 256)
      for (let oy = -256; oy <= 256; oy += 256) {
        g.beginPath();
        g.arc(x + ox, y + oy, rad, 0, TAU);
        g.fill();
      }
  }
  const t = new CanvasTexture(cv);
  t.wrapS = t.wrapT = RepeatWrapping;
  t.colorSpace = SRGBColorSpace;
  return t;
}

/** Aircraft panel lines and rivets (GDD §3.6; concept L170). */
export function panelTex(base: string, seed: number): CanvasTexture {
  const cv = canvas(256, 256);
  const g = ctx(cv);
  const r = mulberry32(seed);
  g.fillStyle = base;
  g.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 46; i++) {
    const x = Math.floor(r() * 16) * 16;
    const y = Math.floor(r() * 16) * 16;
    const w = 16 * (1 + Math.floor(r() * 4));
    const h = 16 * (1 + Math.floor(r() * 3));
    g.fillStyle =
      r() > 0.5 ? 'rgba(255,255,255,' + (r() * 0.07).toFixed(3) + ')' : 'rgba(0,0,0,' + (r() * 0.08).toFixed(3) + ')';
    g.fillRect(x, y, w, h);
  }
  g.strokeStyle = 'rgba(18,22,26,0.55)';
  g.lineWidth = 1.3;
  for (let i = 0; i <= 256; i += 64) {
    g.beginPath();
    g.moveTo(i, 0);
    g.lineTo(i, 256);
    g.stroke();
    g.beginPath();
    g.moveTo(0, i);
    g.lineTo(256, i);
    g.stroke();
  }
  for (let i = 0; i < 18; i++) {
    const sx = Math.floor(r() * 8) * 32;
    const sy = Math.floor(r() * 8) * 32;
    g.strokeRect(sx + 0.5, sy + 0.5, 32, 16 + Math.floor(r() * 2) * 16);
  }
  g.fillStyle = 'rgba(20,24,28,0.5)';
  for (let i = 0; i < 256; i += 8) {
    g.fillRect(i, 62, 1.5, 1.5);
    g.fillRect(i, 190, 1.5, 1.5);
    g.fillRect(126, i, 1.5, 1.5);
  }
  const t = new CanvasTexture(cv);
  t.wrapS = t.wrapT = RepeatWrapping;
  t.colorSpace = SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

/** Soft sprites: a round dot (fire, sparks, glows), smoke puffs and cloud billows (concept L185). */
export function softTex(kind: 'dot' | 'smoke' | 'cloud'): CanvasTexture {
  const cv = canvas(128, 128);
  const g = ctx(cv);
  const r = mulberry32(kind === 'smoke' ? 5 : kind === 'cloud' ? 9 : 3);
  if (kind === 'dot') {
    const gr = g.createRadialGradient(64, 64, 0, 64, 64, 64);
    gr.addColorStop(0, 'rgba(255,255,255,1)');
    gr.addColorStop(0.3, 'rgba(255,255,255,0.7)');
    gr.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = gr;
    g.fillRect(0, 0, 128, 128);
  } else {
    const n = kind === 'cloud' ? 26 : 14;
    for (let i = 0; i < n; i++) {
      const a = r() * TAU;
      const d = r() * (kind === 'cloud' ? 34 : 26);
      const x = 64 + Math.cos(a) * d;
      const y = 64 + Math.sin(a) * d * (kind === 'cloud' ? 0.7 : 1);
      const rad = 18 + r() * 26;
      const gg = g.createRadialGradient(x, y, 0, x, y, rad);
      gg.addColorStop(0, 'rgba(255,255,255,' + (kind === 'cloud' ? 0.42 : 0.55) + ')');
      gg.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = gg;
      g.fillRect(0, 0, 128, 128);
    }
  }
  const t = new CanvasTexture(cv);
  t.colorSpace = SRGBColorSpace;
  return t;
}

/** Deck marking with hazard stripes, e.g. the fuel barge's "FUEL" (GDD §3.6; concept L202). */
export function labelTex(text: string, bg: string, fg: string, accent: string): CanvasTexture {
  const cv = canvas(256, 128);
  const g = ctx(cv);
  g.fillStyle = bg;
  g.fillRect(0, 0, 256, 128);
  g.fillStyle = accent;
  for (let i = -2; i < 12; i++) {
    g.beginPath();
    g.moveTo(i * 24, 0);
    g.lineTo(i * 24 + 12, 0);
    g.lineTo(i * 24 - 4, 16);
    g.lineTo(i * 24 - 16, 16);
    g.fill();
    g.beginPath();
    g.moveTo(i * 24, 112);
    g.lineTo(i * 24 + 12, 112);
    g.lineTo(i * 24 - 4, 128);
    g.lineTo(i * 24 - 16, 128);
    g.fill();
  }
  g.fillStyle = fg;
  g.font = 'bold 64px sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(text, 128, 66);
  const t = new CanvasTexture(cv);
  t.colorSpace = SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

/** V-shaped boat wake (GDD §3.4; concept L209). */
export function wakeTex(): CanvasTexture {
  const cv = canvas(128, 256);
  const g = ctx(cv);
  const r = mulberry32(21);
  for (let i = 0; i < 900; i++) {
    const t = r();
    const side = r() > 0.5 ? 1 : -1;
    const spread = 0.12 + t * 0.85;
    let x = 64 + side * spread * 60 * (0.85 + r() * 0.3);
    const y = t * 250 + 4;
    if (r() < 0.35) x = 64 + (r() - 0.5) * (10 + t * 34);
    const a = (1 - t) * (0.25 + r() * 0.5);
    const rad = 1 + r() * 3.5 * (0.5 + t);
    g.fillStyle = 'rgba(255,255,255,' + a.toFixed(3) + ')';
    g.beginPath();
    g.ellipse(x, y, rad, rad * 1.6, 0, 0, TAU);
    g.fill();
  }
  const tx = new CanvasTexture(cv);
  tx.colorSpace = SRGBColorSpace;
  return tx;
}

export interface TextureSet {
  n1: CanvasTexture;
  n2: CanvasTexture;
  foam: CanvasTexture;
  ground: CanvasTexture;
  dot: CanvasTexture;
  smoke: CanvasTexture;
  cloud: CanvasTexture;
  wake: CanvasTexture;
  body: CanvasTexture;
  wing: CanvasTexture;
  label: CanvasTexture;
}

/** The concept's texture set, created in the concept's order with its repeats (concept L671–L673). */
export function createTextures(): TextureSet {
  const TX: TextureSet = {
    n1: waterNormalTex(3, 256),
    n2: waterNormalTex(9, 256),
    foam: foamTex(),
    ground: groundTex(),
    dot: softTex('dot'),
    smoke: softTex('smoke'),
    cloud: softTex('cloud'),
    wake: wakeTex(),
    body: panelTex('#9aa3ab', 5),
    wing: panelTex('#8b949d', 8),
    label: labelTex('FUEL', '#2a2e33', '#f4f1ea', '#e8772e'),
  };
  TX.body.repeat.set(3, 5);
  TX.wing.repeat.set(0.24, 0.24);
  return TX;
}
